// @flow

/**
 * Zone Utilities
 * @module util/zone
 */

import Copy from '../assets/copy.json'
import { Config } from '../util/config'

// IMAGES //////////////////////////////////////////////////////////////////////////

import AnnualsImg from '../assets/images/ZoneCropTypes/Annuals/annuals.png'
import PerennialsImg from '../assets/images/ZoneCropTypes/Perennials/perennials.png'
import ShrubsImg from '../assets/images/ZoneCropTypes/Shrubs/shrubs.png'
import TreesImg from '../assets/images/ZoneCropTypes/Trees/trees.png'
import GrassImg from '../assets/images/ZoneCropTypes/Grass/grass-warm-or-cool.png'
import XeriscapeImg from '../assets/images/ZoneCropTypes/Xeriscape/xeriscape.png'
import GardenImg from '../assets/images/ZoneCropTypes/garden.png'

// ICONS ////////////////////////////////////////////////////////////////////////////

import CoarseIcon from '../assets/icons/Zones/soilType/coarse.svg'
import FineIcon from '../assets/icons/Zones/soilType/fine.svg'
import MediumIcon from '../assets/icons/Zones/soilType/medium.svg'

import AnnualIcon from '../assets/icons/Zones/zoneType/annuals.svg'
import CoolSeasonIcon from '../assets/icons/Zones/zoneType/coolseason.svg'
import PerennialIcon from '../assets/icons/Zones/zoneType/perennial.svg'
import ShrubsIcon from '../assets/icons/Zones/zoneType/shrubs.svg'
import TreesIcon from '../assets/icons/Zones/zoneType/trees.svg'
import WarmSeasonIcon from '../assets/icons/Zones/zoneType/warmseason.svg'
import XeriscapeIcon from '../assets/icons/Zones/zoneType/xeriscape.svg'
import GardenIcon from '../assets/icons/Zones/zoneType/garden.svg'

import FlatIcon from '../assets/icons/Zones/slope/flat.svg'
import SlightIcon from '../assets/icons/Zones/slope/slight.svg'
import ModerateIcon from '../assets/icons/Zones/slope/moderate.svg'
import SteepIcon from '../assets/icons/Zones/slope/steep.svg'

import BubblerIcon from '../assets/icons/Zones/nozzle/ic_bubbler_gray-1_40px.svg'
import EmitterIcon from '../assets/icons/Zones/nozzle/ic_emitter_gray-1_40px.svg'
import FixedIcon from '../assets/icons/Zones/nozzle/ic_fixed-spray_gray-1_40px.svg'
import MisterIcon from '../assets/icons/Zones/nozzle/ic_mister_gray-1_40px.svg'
import RotorIcon from '../assets/icons/Zones/nozzle/ic_rotor_gray-1_40px.svg'
import RotaryIcon from '../assets/icons/Zones/nozzle/ic_rotary-nozzle_gray-1_40px.svg'
import DriplineIcon from '../assets/icons/Zones/nozzle/dripline.svg'

import LotsOfShadeIcon from '../assets/icons/Zones/exposure/lotsofshade.svg'
import MostlyShadeIcon from '../assets/icons/Zones/exposure/mostlyshade.svg'
import SomeShadeIcon from '../assets/icons/Zones/exposure/someshade.svg'
import LotsOfSunIcon from '../assets/icons/Zones/exposure/lotsofsun.svg'

// TYPES //////////////////////////////////////////////////////////////////////////

import type {
  Alert,
  CropType,
  NozzleType,
  SoilType,
  SlopeType,
  ExposureType,
  Zone,
  ZoneModel
} from '../ducks/zone/types'

// DEFAULT CONSTANTS ///////////////////////////////////////////////////////

/** Default zone run time IN SECONDS */
export const DEFAULT_ZONE_RUN_DURATION = 180

/** Default zone delay in minutes */
export const DEFAULT_ZONE_DELAY_MINUTES: 20 = 20

/** Min zone delay in minutes */
export const MIN_ZONE_DELAY_MINUTES: 1 = 1

/** Max zone delay in minutes */
export const MAX_ZONE_DELAY_MINUTES: 600 = 600

// Slope Types ////////////////////////////////////////////////////////////////

type SlopeTypes = Map<string, string>

const slopeTypes: SlopeTypes = new Map([
  ['ZERO_THREE', 'Flat'],
  ['FOUR_SIX', 'Slight'],
  ['SEVEN_TWELVE', 'Moderate'],
  ['OVER_TWELVE', 'Steep']
])

// Crop Types /////////////////////////////////////////////////////////////////

type CropTypeObject = { id: string, name: string, order: number }
type CropTypes = Map<CropType, CropTypeObject>
/**
 * Map of all possible crop objects
 * @type {Map}
 * @const
 */
const cropTypes: CropTypes = new Map([
  [
    'COOL_SEASON_GRASS',
    {
      id: 'COOL_SEASON_GRASS',
      name: 'Cool Season Grass',
      order: 0
    }
  ],
  [
    'WARM_SEASON_GRASS',
    {
      id: 'WARM_SEASON_GRASS',
      name: 'Warm Season Grass',
      order: 1
    }
  ],
  [
    'SHRUBS',
    {
      id: 'SHRUBS',
      name: 'Shrubs',
      order: 2
    }
  ],
  [
    'TREES',
    {
      id: 'TREES',
      name: 'Trees',
      order: 4
    }
  ],
  [
    'ANNUAL',
    {
      id: 'ANNUAL',
      name: 'Annual',
      order: 5
    }
  ],
  [
    'PERENNIAL',
    {
      id: 'PERENNIAL',
      name: 'Perennial',
      order: 6
    }
  ],
  [
    'XERISCAPE',
    {
      id: 'XERISCAPE',
      name: 'Xeriscape',
      order: 7
    }
  ],
  [
    'GARDEN',
    {
      id: 'GARDEN',
      name: 'Garden',
      order: 8
    }
  ]
])

/**
 * Sort objects by their `cropType` order
 * @param  {object} a - Object with `cropType` property
 * @param  {object} b - Object with `cropType` property
 * @return {number} - Sort order
 */
export const sortZonesByCropType = (
  a: { cropType: CropType },
  b: { cropType: CropType }
): number => {
  let retVal = 0
  const zoneACropObject: CropTypeObject = getCropType(a.cropType)
  const zoneBCropObject: CropTypeObject = getCropType(b.cropType)

  if (zoneACropObject.order < zoneBCropObject.order) {
    retVal = -1
  } else if (zoneACropObject.order > zoneBCropObject.order) {
    retVal = 1
  }

  return retVal
}

/**
 * Sort zones by zoneNumber
 * @param {array} zones - zones
 * @returns {array} - zones in order
 */
export const sortZonesByZoneNumber = (zones: Array<ZoneModel>) => {
  // $FlowFixMe
  return zones.sort((a: { zoneNumber: number }, b: { zoneNumber: number }):
    | -1
    | 0
    | 1 => {
    return a.zoneNumber > b.zoneNumber ? 1 : -1
  })
}

/**
 * Sort zones by cropType then zoneNumber
 * @param {array} zones - zones
 * @returns {array} - zones in order
 */
export const sortZonesByCropTypeThenZoneNumber = (zones: Array<Zone>) => {
  let sortedZones = []
  const zonesByCropType = getZonesByCropType(zones)

  Object.keys(zonesByCropType).map(cropType => {
    sortedZones = sortedZones.concat(
      sortZonesByZoneNumber(zonesByCropType[cropType])
    )
  })

  return sortedZones
}

// Soil Types /////////////////////////////////////////////////////////////////

type SoilTypeObject = { id: SoilType, name: string, icon: string }
type SoilTypes = Map<SoilType, SoilTypeObject>

/**
 * Basic soil type data; order matters
 * @see {@link https://rachio.atlassian.net/browse/PG-332}
 */
const soilTypes: SoilTypes = new Map([
  // These icons are just placeholders; real icons are likely coming soon
  ['SAND', { id: 'SAND', name: 'Sand', icon: CoarseIcon }],
  ['LOAMY_SAND', { id: 'LOAMY_SAND', name: 'Loamy Sand', icon: MediumIcon }],
  ['SANDY_LOAM', { id: 'SANDY_LOAM', name: 'Sandy Loam', icon: MediumIcon }],
  ['LOAM', { id: 'LOAM', name: 'Loam', icon: MediumIcon }],
  ['CLAY_LOAM', { id: 'CLAY_LOAM', name: 'Clay Loam', icon: MediumIcon }],
  ['SILTY_CLAY', { id: 'SILTY_CLAY', name: 'Silty Clay', icon: MediumIcon }],
  ['CLAY', { id: 'CLAY', name: 'Clay', icon: FineIcon }]
])

//////// EDIT ZONE DETAILS RADIO BUTTON OPTIONS ///////////////////////////////

export type SlopeTypeRadioOption = {
  id: SlopeType,
  icon: string,
  name: string,
  secondaryText: string,
  value: SlopeType
}
type SlopeTypeRadioOptions = Map<SlopeType, SlopeTypeRadioOption>

const slopeTypeRadioOptions: SlopeTypeRadioOptions = new Map([
  [
    'ZERO_THREE',
    {
      id: 'ZERO_THREE',
      icon: FlatIcon,
      name: Copy.zone.edit.sections.slope.options.flat.name,
      secondaryText: Copy.zone.edit.sections.slope.options.flat.secondaryText,
      value: 'ZERO_THREE'
    }
  ],
  [
    'FOUR_SIX',
    {
      id: 'FOUR_SIX',
      icon: SlightIcon,
      name: Copy.zone.edit.sections.slope.options.slight.name,
      secondaryText: Copy.zone.edit.sections.slope.options.slight.secondaryText,
      value: 'FOUR_SIX'
    }
  ],
  [
    'SEVEN_TWELVE',
    {
      id: 'SEVEN_TWELVE',
      icon: ModerateIcon,
      name: Copy.zone.edit.sections.slope.options.moderate.name,
      secondaryText:
        Copy.zone.edit.sections.slope.options.moderate.secondaryText,
      value: 'SEVEN_TWELVE'
    }
  ],
  [
    'OVER_TWELVE',
    {
      id: 'OVER_TWELVE',
      icon: SteepIcon,
      name: Copy.zone.edit.sections.slope.options.steep.name,
      secondaryText: Copy.zone.edit.sections.slope.options.steep.secondaryText,
      value: 'OVER_TWELVE'
    }
  ]
])

export type SoilTypeRadioOption = {
  id: SoilType,
  icon: ?string,
  name: string,
  secondaryText: string,
  value: SoilType,
  explanation: string,
  bottomsheetTitle: string
}
type SoilTypeRadioOptions = Map<SoilType, SoilTypeRadioOption>

const soilTypeRadioOptions: SoilTypeRadioOptions = new Map(
  Array.from(soilTypes.values()).map((st: SoilTypeObject) => [
    st.id,
    {
      id: st.id,
      icon: null,
      name: st.name,
      secondaryText: '',
      value: st.id,
      explanation: '',
      bottomsheetTitle: ''
    }
  ])
)

export type NozzleTypeRadioOption = {
  bottomsheetTitle: string,
  explanation: string,
  icon: string,
  id: NozzleType,
  media: string,
  name: string,
  secondaryText: string,
  value: NozzleType
}
type NozzleTypeRadioOptions = Map<NozzleType, NozzleTypeRadioOption>

export const nozzleTypeRadioOptions: NozzleTypeRadioOptions = new Map([
  [
    'ROTOR_HEAD',
    {
      id: 'ROTOR_HEAD',
      icon: RotorIcon,
      name: Copy.zone.edit.sections.sprayHead.options.rotor.name,
      secondaryText:
        Copy.zone.edit.sections.sprayHead.options.rotor.secondaryText,
      value: 'ROTOR_HEAD',
      media:
        Config.AWS_S3_MEDIA_VIDEO + '/video/nozzle/zone-nozzle-rotor-head.webm',
      explanation: Copy.zone.edit.sections.sprayHead.options.rotor.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.sprayHead.options.rotor.bottomsheetTitle
    }
  ],
  [
    'FIXED_SPRAY_HEAD',
    {
      id: 'FIXED_SPRAY_HEAD',
      icon: FixedIcon,
      name: Copy.zone.edit.sections.sprayHead.options.fixed.name,
      secondaryText:
        Copy.zone.edit.sections.sprayHead.options.fixed.secondaryText,
      value: 'FIXED_SPRAY_HEAD',
      media:
        Config.AWS_S3_MEDIA_VIDEO +
        '/video/nozzle/zone-nozzle-fixed-spray.webm',
      explanation: Copy.zone.edit.sections.sprayHead.options.fixed.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.sprayHead.options.fixed.bottomsheetTitle
    }
  ],
  [
    'ROTARY_NOZZLE',
    {
      id: 'ROTARY_NOZZLE',
      icon: RotaryIcon,
      name: Copy.zone.edit.sections.sprayHead.options.rotary.name,
      secondaryText:
        Copy.zone.edit.sections.sprayHead.options.rotary.secondaryText,
      value: 'ROTARY_NOZZLE',
      media:
        Config.AWS_S3_MEDIA_VIDEO +
        '/video/nozzle/zone-nozzle-rotary-nozzle.webm',
      explanation: Copy.zone.edit.sections.sprayHead.options.rotary.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.sprayHead.options.rotary.bottomsheetTitle
    }
  ],
  [
    'MISTER',
    {
      id: 'MISTER',
      icon: MisterIcon,
      name: Copy.zone.edit.sections.sprayHead.options.mister.name,
      secondaryText:
        Copy.zone.edit.sections.sprayHead.options.mister.secondaryText,
      value: 'MISTER',
      media:
        Config.AWS_S3_MEDIA_VIDEO + '/video/nozzle/zone-nozzle-mister.webm',
      explanation: Copy.zone.edit.sections.sprayHead.options.mister.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.sprayHead.options.mister.bottomsheetTitle
    }
  ],
  [
    'BUBBLER',
    {
      id: 'BUBBLER',
      icon: BubblerIcon,
      name: Copy.zone.edit.sections.sprayHead.options.bubbler.name,
      secondaryText:
        Copy.zone.edit.sections.sprayHead.options.bubbler.secondaryText,
      value: 'BUBBLER',
      media:
        Config.AWS_S3_MEDIA_VIDEO + '/video/nozzle/zone-nozzle-bubbler.webm',
      explanation:
        Copy.zone.edit.sections.sprayHead.options.bubbler.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.sprayHead.options.bubbler.bottomsheetTitle
    }
  ],
  [
    'EMITTER',
    {
      id: 'EMITTER',
      icon: EmitterIcon,
      name: Copy.zone.edit.sections.sprayHead.options.emitter.name,
      secondaryText:
        Copy.zone.edit.sections.sprayHead.options.emitter.secondaryText,
      value: 'EMITTER',
      media:
        Config.AWS_S3_MEDIA_VIDEO + '/video/nozzle/zone-nozzle-emitter.webm',
      explanation:
        Copy.zone.edit.sections.sprayHead.options.emitter.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.sprayHead.options.emitter.bottomsheetTitle
    }
  ],
  [
    'DRIPLINE',
    {
      id: 'DRIPLINE',
      icon: DriplineIcon,
      name: 'Dripline',
      secondaryText: '',
      value: 'DRIPLINE',
      media:
        Config.AWS_S3_MEDIA_VIDEO + '/video/nozzle/zone-nozzle-dripline.webm',
      explanation: '',
      bottomsheetTitle: 'Dripline'
    }
  ]
])

export type ExposureTypeRadioOption = {
  id: ExposureType,
  icon: string,
  name: string,
  secondaryText: string,
  value: ExposureType
}
type ExposureTypeRadioOptions = Map<ExposureType, ExposureTypeRadioOption>

const exposureTypeRadioOptions: ExposureTypeRadioOptions = new Map([
  [
    'LOTS_OF_SUN',
    {
      id: 'LOTS_OF_SUN',
      icon: LotsOfSunIcon,
      name: Copy.zone.edit.sections.exposure.options.lotsOfSun.name,
      secondaryText:
        Copy.zone.edit.sections.exposure.options.lotsOfSun.secondaryText,
      value: 'LOTS_OF_SUN'
    }
  ],
  [
    'SOME_SHADE',
    {
      id: 'SOME_SHADE',
      icon: SomeShadeIcon,
      name: Copy.zone.edit.sections.exposure.options.someShade.name,
      secondaryText:
        Copy.zone.edit.sections.exposure.options.someShade.secondaryText,
      value: 'SOME_SHADE'
    }
  ],
  [
    'LOTS_OF_SHADE',
    {
      id: 'LOTS_OF_SHADE',
      icon: LotsOfShadeIcon,
      name: Copy.zone.edit.sections.exposure.options.lotsOfShade.name,
      secondaryText:
        Copy.zone.edit.sections.exposure.options.lotsOfShade.secondaryText,
      value: 'LOTS_OF_SHADE'
    }
  ],
  [
    'MOSTLY_SHADE',
    {
      id: 'MOSTLY_SHADE',
      icon: MostlyShadeIcon,
      name: Copy.zone.edit.sections.exposure.options.mostlyShade.name,
      secondaryText:
        Copy.zone.edit.sections.exposure.options.mostlyShade.secondaryText,
      value: 'MOSTLY_SHADE'
    }
  ]
])

export type CropTypeRadioOption = {
  id: CropType,
  icon: string,
  name: string,
  secondaryText: string,
  value: CropType,
  explanation: string,
  bottomsheetTitle: string
}
type CropTypeRadioOptions = Map<CropType, CropTypeRadioOption>

export const cropTypeRadioOptions: CropTypeRadioOptions = new Map([
  [
    'COOL_SEASON_GRASS',
    {
      id: 'COOL_SEASON_GRASS',
      icon: CoolSeasonIcon,
      name: Copy.zone.edit.sections.zoneType.options.coolSeasonGrass.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.coolSeasonGrass.secondaryText,
      value: 'COOL_SEASON_GRASS',
      media: GrassImg,
      explanation:
        Copy.zone.edit.sections.zoneType.options.coolSeasonGrass.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.coolSeasonGrass
          .bottomsheetTitle
    }
  ],
  [
    'WARM_SEASON_GRASS',
    {
      id: 'WARM_SEASON_GRASS',
      icon: WarmSeasonIcon,
      name: Copy.zone.edit.sections.zoneType.options.warmSeasonGrass.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.warmSeasonGrass.secondaryText,
      value: 'WARM_SEASON_GRASS',
      media: GrassImg,
      explanation:
        Copy.zone.edit.sections.zoneType.options.warmSeasonGrass.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.warmSeasonGrass
          .bottomsheetTitle
    }
  ],
  [
    'SHRUBS',
    {
      id: 'SHRUBS',
      icon: ShrubsIcon,
      name: Copy.zone.edit.sections.zoneType.options.shrubs.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.shrubs.secondaryText,
      value: 'SHRUBS',
      media: ShrubsImg,
      explanation: Copy.zone.edit.sections.zoneType.options.shrubs.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.shrubs.bottomsheetTitle
    }
  ],
  [
    'TREES',
    {
      id: 'TREES',
      icon: TreesIcon,
      name: Copy.zone.edit.sections.zoneType.options.trees.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.trees.secondaryText,
      value: 'TREES',
      media: TreesImg,
      explanation: Copy.zone.edit.sections.zoneType.options.trees.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.trees.bottomsheetTitle
    }
  ],
  [
    'ANNUAL',
    {
      id: 'ANNUAL',
      icon: AnnualIcon,
      name: Copy.zone.edit.sections.zoneType.options.annual.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.annual.secondaryText,
      value: 'ANNUAL',
      media: AnnualsImg,
      explanation: Copy.zone.edit.sections.zoneType.options.annual.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.annual.bottomsheetTitle
    }
  ],
  [
    'PERENNIAL',
    {
      id: 'PERENNIAL',
      icon: PerennialIcon,
      name: Copy.zone.edit.sections.zoneType.options.perennial.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.perennial.secondaryText,
      value: 'PERENNIAL',
      media: PerennialsImg,
      explanation:
        Copy.zone.edit.sections.zoneType.options.perennial.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.perennial.bottomsheetTitle
    }
  ],
  [
    'XERISCAPE',
    {
      id: 'XERISCAPE',
      icon: XeriscapeIcon,
      name: Copy.zone.edit.sections.zoneType.options.xeriscape.name,
      secondaryText:
        Copy.zone.edit.sections.zoneType.options.xeriscape.secondaryText,
      value: 'XERISCAPE',
      media: XeriscapeImg,
      explanation:
        Copy.zone.edit.sections.zoneType.options.xeriscape.explanation,
      bottomsheetTitle:
        Copy.zone.edit.sections.zoneType.options.xeriscape.bottomsheetTitle
    }
  ],
  [
    'GARDEN',
    {
      id: 'GARDEN',
      icon: GardenIcon,
      name: 'Garden',
      value: 'GARDEN',
      media: GardenImg,
      secondaryText: '',
      explanation:
        'Garden with vegetables or plants that have high water needs.',
      bottomsheetTitle: 'Garden'
    }
  ]
])

type RadioOptions =
  | SlopeTypeRadioOptions
  | SoilTypeRadioOptions
  | NozzleTypeRadioOptions
  | ExposureTypeRadioOptions
  | CropTypeRadioOptions
type ZoneAttribute =
  | 'CROPTYPE'
  | 'NOZZLETYPE'
  | 'SOILTYPE'
  | 'EXPOSURETYPE'
  | 'SLOPETYPE'
type ZoneAttributes = Map<ZoneAttribute, RadioOptions>

const zoneAttributes: ZoneAttributes = new Map([
  ['CROPTYPE', cropTypeRadioOptions],
  ['NOZZLETYPE', nozzleTypeRadioOptions],
  ['SOILTYPE', soilTypeRadioOptions],
  ['EXPOSURETYPE', exposureTypeRadioOptions],
  ['SLOPETYPE', slopeTypeRadioOptions]
])

/**
 * getRadioOptions - Get all data for each option in each ZoneEdit SelectLists
 * @param {string} type - Zone attribute
 * @returns {object} All data for each radio option
 */
export const getRadioOptions = (type: ZoneAttribute): Array<*> => {
  const zoneAttribute = zoneAttributes.get(type)
  return zoneAttribute ? [...zoneAttribute.values()] : []
}

/**
 * getRadioOptionData - Get a specific data value for a given radioOption
 * @param {string} type - Zone attribute
 * @param {string} option - Zone attribute radioOption
 * @param {string} dataType - The desired data value from the radioOption
 * @returns {string} - A specific data value for a zoneAttribute's radioOption.
 */
export const getRadioOptionData = (
  type: ZoneAttribute,
  option: CropType | ExposureType | NozzleType | SlopeType | SoilType | string,
  dataType: string
): string => {
  const zoneAttribute = zoneAttributes.get(type)
  // $FlowFixMe
  const data = zoneAttribute && zoneAttribute.get(option)

  if (data) {
    return data[dataType]
  }
  return ''
}

/**
 * getSlopeType - Get slope type object
 *
 * @param {string} slopeId Slopetype from server
 *
 * @returns {string} slopeType Slopetype for UI
 */
export const getSlopeType = (slopeId: string) => slopeTypes.get(slopeId)

/**
 * Get crop type object
 * @function
 * @param  {string} cropTypeId - Crop type ID
 * @return {object} - Crop type object
 */
export const getCropType = (cropTypeId: CropType): CropTypeObject =>
  ((cropTypes.get(cropTypeId): any): CropTypeObject)

/**
 * Get an array of all crop type ids
 * @return {string[]} - Array of crop type ids
 */
/* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.85.0. To view the error, delete this comment and run Flow. */
export const getCropTypeIds = () => Array.from(cropTypes.keys())

/**
 * Get an object of zoneDetails grouped by crop type id (sorted by default croptype sort)
 * @param {object[]} zoneDetailArr - Array of zoneDetails
 * @return {object} - Object of arrays of zoneDetails, keyed by crop type
 */
export const getZonesByCropType = (zoneDetailArr: Array<Object>) => {
  const sortedZones = {}
  const zoneDetailArrClone = Array.from(zoneDetailArr).sort(sortZonesByCropType)

  zoneDetailArrClone.forEach(zoneDetail => {
    if (!sortedZones.hasOwnProperty(zoneDetail.cropType)) {
      sortedZones[zoneDetail.cropType] = []
    }
    sortedZones[zoneDetail.cropType].push(zoneDetail)
  })

  return sortedZones
}

//////// ZONE DETAILS COMPUTED PROPERTIES //////////////////////////////

/**
 * Boolean zone computed property isDisabledDueToHighFlow
 * @param  {object} zone - ZoneDetail object
 * @return  {boolean} - isDisabledDueToHighFlow
 */
export const isDisabledDueToHighFlow = (zone: ZoneModel | null): boolean => {
  return Boolean(
    zone && !zone.enabled && zone.zoneDisabledReason === 'HIGH_FLOW'
  )
}

/**
 * Boolean zone computed property isDisabledDueToLowFlow
 * @param  {object} zone - ZoneDetail object
 * @return  {boolean} - isDisabledDueToLowFlow
 */
export const isDisabledDueToLowFlow = (zone: ZoneModel | null): boolean => {
  return Boolean(
    zone && !zone.enabled && zone.zoneDisabledReason === 'LOW_FLOW'
  )
}

/**
 * Boolean zone computed property isDisabledBySystem
 * @param  {object} zone - ZoneDetail object
 * @return  {boolean} - isDisabledBySystem
 */
export const isDisabledBySystem = (zone: ZoneModel | null): boolean => {
  return isDisabledDueToHighFlow(zone) || isDisabledDueToLowFlow(zone)
}

/**
 * Get Alert text for specific zone conditions,
 * disabled due to highFlow, or disabled due to low flow = Priority 0,
 * alerts = Priority 1
 * @param  {object} zone - Zone object
 * @param  {boolean} short - return shortened strings if true
 * @return {string | null} - An alert string or null
 */
export const getOptionalDisabledBySystemMessage = (
  zone: ZoneModel | null,
  short: boolean = false
): ?string => {
  const copy = short ? Copy.zone.alert.disabledShort : Copy.zone.alert.disabled

  if (isDisabledDueToHighFlow(zone)) return copy.high
  if (isDisabledDueToLowFlow(zone)) return copy.low

  return null
}

/**
 * get latest alert title
 * @param  {object} zone - ZoneDetail
 * @return {string | null} - -1 or 1
 */
export const getOptionalLatestAlertMessage = (
  zone: ZoneModel | null
): ?string => {
  if (!zone || !zone.alerts || zone.alerts.length === 0) return null

  const copy = Copy.zone.alert.types
  const sorted = zone.alerts.sort(sortByCreated)

  return copy[sorted[0].type]
}

/**
 * Boolean zone computed property - is nozzle a drip type
 * @param  {object} zone - Zone object
 * @return  {boolean} - isDripZone
 */
export const isDripZone = (zone: Zone): boolean => {
  return (
    zone.nozzleType == 'EMITTER' ||
    zone.nozzleType == 'BUBBLER' ||
    zone.nozzleType == 'MISTER' ||
    zone.nozzleType == 'DRIPLINE'
  )
}

/**
 * Sort by timestamp, greatest to least
 * @param  {object} a - alert
 * @param  {object} b - alert
 * @return {number} - -1 or 1
 */
const sortByCreated = (a: Alert, b: Alert) => {
  let retVal = 0
  // $FlowFixMe
  if (a.created > b.created) {
    retVal = -1
    // $FlowFixMe
  } else if (a.created < b.created) {
    retVal = 1
  }
  return retVal
}

// Ducks //////////////////////////////////////////////////////////////////////

/**
 * wrapStringForServer - Some zone values must be wrapped in an object for the server
 * @param {object} data - Data to be sent to server.
 * @return {object} Corrected data with necessary wrappers.
 */
export const wrapStringForServer = (data: Object) => {
  const wrappedData = { ...data }

  for (const e in data) {
    if (
      [
        'soilType',
        'cropType',
        'nozzleType',
        'exposureType',
        'slopeType'
      ].indexOf(e) !== -1 &&
      data[e].typeOf !== 'object'
    ) {
      wrappedData[e] = { value: wrappedData[e] }
    }
  }
  return wrappedData
}

// Images /////////////////////////////////////////////////////////////////////

/**
 * Create Image URL from image ID
 * @param  {string} imageID - Image ID
 * @return {string} - Image URL
 */
export const getImageURL = (imageID: string): string =>
  Config.AWS_S3_MEDIA_PHOTO + '/' + imageID

// Various ////////////////////////////////////////////////////////////////////

/**
 * Determine if zone is in a group of schedules
 * @param  {object[]} schedules - Array of schedule objects
 * @param  {string} zoneId - Zone ID
 * @return {boolean} - Whether or not the zone is in one of the passed in schedules
 */
export const zoneNotInSchedules = (schedules: Array<Object>, zoneId: string) =>
  schedules
    .filter(schedule => schedule.zoneInfo.find(zone => zone.zoneId === zoneId))
    .filter(sched => sched).length === 0

/**
 * Check if `zoneId` included in any genius schedules passed in
 * @param  {object[]} schedules - Array of schedule objects
 * @param  {string} zoneId - Zone ID
 * @return {boolean} - Whether or not the zone is in one of the genius schedules passed in
 */
export const zoneNotInFlexDailySchedules = (
  schedules: Array<Object>,
  zoneId: string
) =>
  zoneNotInSchedules(
    schedules.filter(
      schedule => schedule.scheduleCriteria.scheduleType === 'FLEX_DAILY'
    ),
    zoneId
  )
